import axios from "axios";
import { API_URL } from "../utils/Constant";

/**
 * Serialize Lab Ops query params.
 * Multi-select values become repeated keys (e.g. test_id=1&test_id=2).
 * Empty / null / false values are omitted.
 */
const buildLabOpsQueryParams = (params = {}) => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === "") {
      return;
    }

    if (typeof value === "boolean") {
      if (value) {
        searchParams.append(key, "true");
      }
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item == null || item === "") {
          return;
        }
        searchParams.append(key, String(item));
      });
      return;
    }

    searchParams.append(key, String(value));
  });

  return searchParams;
};

/**
 * Overdue list + phase buckets for table and charts.
 * @param {object} params — includes required date_range; never send rca from Overdue UI
 * @param {AbortSignal} [signal]
 */
export const getOverdueByPhase = async (params, signal = null) => {
  try {
    const response = await axios.get(`${API_URL}/overdue_by_phase/`, {
      params: buildLabOpsQueryParams(params),
      signal,
    });
    return { success: true, canceled: false, ...response.data };
  } catch (e) {
    return {
      success: false,
      message: e.response?.data?.message || "Something went wrong.",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Overdue drill-down / Excel — scoped overdue_by_phase_export.
 * @param {object} params
 * @param {AbortSignal} [signal]
 */
export const getOverdueByPhaseExport = async (params, signal = null) => {
  try {
    const response = await axios.get(`${API_URL}/overdue_by_phase_export/`, {
      params: buildLabOpsQueryParams(params),
      signal,
    });
    return { success: true, canceled: false, ...response.data };
  } catch (e) {
    return {
      success: false,
      message: e.response?.data?.message || "Something went wrong.",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Overdue SLA trend by day | week | month.
 * @param {object} params — same filters as overdue plus required aggregate
 * @param {AbortSignal} [signal]
 */
export const getOverdueSla = async (params, signal = null) => {
  try {
    const response = await axios.get(`${API_URL}/overdue_sla/`, {
      params: buildLabOpsQueryParams(params),
      signal,
    });
    return { success: true, canceled: false, ...response.data };
  } catch (e) {
    return {
      success: false,
      message: e.response?.data?.message || "Something went wrong.",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Retest / 6000-series traceability rows for table and charts.
 * @param {object} params
 * @param {AbortSignal} [signal]
 */
export const getRetestTraceability = async (params, signal = null) => {
  try {
    const response = await axios.get(`${API_URL}/retest_traceability/`, {
      params: buildLabOpsQueryParams(params),
      signal,
    });
    return { success: true, canceled: false, ...response.data };
  } catch (e) {
    return {
      success: false,
      message: e.response?.data?.message || "Something went wrong.",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Retest drill-down / Excel — scoped retest_traceability_export.
 * @param {object} params
 * @param {AbortSignal} [signal]
 */
export const getRetestTraceabilityExport = async (params, signal = null) => {
  try {
    const response = await axios.get(`${API_URL}/retest_traceability_export/`, {
      params: buildLabOpsQueryParams(params),
      signal,
    });
    return { success: true, canceled: false, ...response.data };
  } catch (e) {
    return {
      success: false,
      message: e.response?.data?.message || "Something went wrong.",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * RCA dashboard (grouped or flat — return shape used as-is by callers).
 * @param {object} params
 * @param {AbortSignal} [signal]
 */
export const getRcaDashboard = async (params, signal = null) => {
  try {
    const response = await axios.get(`${API_URL}/rca_dashboard/`, {
      params: buildLabOpsQueryParams(params),
      signal,
    });
    return { success: true, canceled: false, ...response.data };
  } catch (e) {
    return {
      success: false,
      message: e.response?.data?.message || "Something went wrong.",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Reasons catalog. Pass categories like ["standard", "labops"] or ["retest"].
 * @param {string[]} [categories]
 * @param {AbortSignal} [signal]
 */
export const getLabOpsReasons = async (
  categories = ["standard", "labops"],
  signal = null,
) => {
  try {
    const response = await axios.get(`${API_URL}/reasons/`, {
      params: buildLabOpsQueryParams({ reasons_category: categories }),
      signal,
    });
    return { success: true, canceled: false, ...response.data };
  } catch (e) {
    return {
      success: false,
      message: e.response?.data?.message || "Something went wrong.",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};
